import { dayOptions } from '@/helpers/days'
import { z } from 'zod'

const MAX_QUERY_LENGTH = 80

const SORT_OPTIONS = ['live', 'upcoming', 'finished']
const DAY_NAMES = dayOptions.map((day) => day.name)

function toSingleValue(value) {
  if (Array.isArray(value)) return value[0]
  return value
}

function parseCsv(value) {
  if (!value) return []

  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => String(entry).split(','))
      .map((entry) => entry.trim())
      .filter(Boolean)
  }

  return String(value)
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function uniq(values) {
  return [...new Set(values)]
}

const baseSchema = z.object({
  q: z
    .string()
    .trim()
    .max(MAX_QUERY_LENGTH)
    .optional()
    .transform((value) => value || ''),
  cursor: z.string().trim().min(1).optional(),
})

const racingSchema = baseSchema.extend({
  sort: z
    .enum(SORT_OPTIONS)
    .optional()
    .transform((value) => value || 'live'),
  platforms: z.array(z.string().min(1)).default([]),
  raceTypes: z.array(z.string().min(1)).default([]),
  carClasses: z.array(z.string().min(1)).default([]),
  days: z.array(z.enum(DAY_NAMES)).default([]),
})

const communitiesSchema = baseSchema.extend({
  platforms: z.array(z.string().min(1)).default([]),
  days: z.array(z.enum(DAY_NAMES)).default([]),
})

const liveriesSchema = baseSchema.extend({
  platforms: z.array(z.string().min(1)).default([]),
  cars: z.array(z.string().min(1)).default([]),
})

export const PAGE_SIZES = {
  racing: 12,
  communities: 24,
  liveries: 12,
}

export const RACING_SORT_OPTIONS = SORT_OPTIONS

export const RACE_TYPES = ['single', 'championship']

function parseSchema(schema, rawParams) {
  const raw = rawParams || {}

  const parsed = {
    q: toSingleValue(raw.q),
    cursor: toSingleValue(raw.cursor),
    sort: toSingleValue(raw.sort),
    platforms: uniq(parseCsv(raw.platforms)),
    raceTypes: uniq(parseCsv(raw.raceTypes)),
    carClasses: uniq(parseCsv(raw.carClasses)),
    days: uniq(parseCsv(raw.days)),
    cars: uniq(parseCsv(raw.cars)),
  }

  const parsedResult = schema.safeParse(parsed)
  if (parsedResult.success) return parsedResult.data

  return schema.parse({})
}

export function parseRacingSearchParams(rawParams) {
  const parsed = parseSchema(racingSchema, rawParams)

  const raceTypes = parsed.raceTypes.filter((type) => RACE_TYPES.includes(type))

  return {
    ...parsed,
    raceTypes,
  }
}

export function parseCommunitiesSearchParams(rawParams) {
  return parseSchema(communitiesSchema, rawParams)
}

export function parseLiveriesSearchParams(rawParams) {
  return parseSchema(liveriesSchema, rawParams)
}

export function buildSearchParamsObject(filter) {
  const params = new globalThis.URLSearchParams()

  if (filter.q) params.set('q', filter.q)
  if (filter.sort && filter.sort !== 'live') params.set('sort', filter.sort)
  if (filter.platforms?.length > 0)
    params.set('platforms', filter.platforms.join(','))
  if (filter.raceTypes?.length > 0)
    params.set('raceTypes', filter.raceTypes.join(','))
  if (filter.carClasses?.length > 0)
    params.set('carClasses', filter.carClasses.join(','))
  if (filter.days?.length > 0) params.set('days', filter.days.join(','))
  if (filter.cars?.length > 0) params.set('cars', filter.cars.join(','))

  return params
}
